import React, { useEffect, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import moment from 'moment'

import { bookSession, getAvailableSessions } from '../../../services/api/snakSammen'

import { CustomText } from '../../text/StyledText'
import NormalIcon from '../../icons/NormalIcon'
import { RoundSquareBtn } from '../../buttons/StyledButtons'
import CustomPressable from "../../pressables/CustomPressable"

import { CornerStyles, ShadowStyles } from '../../../styles'
import Fontsizes from '../../../constants/Fontsizes'
import IconNames from '../../../constants/IconNames'
import Colors from '../../../constants/Colors'
import { txt } from '../../../locales/i18n'


export function AvailableSessions({ availableDates, handleRefresh, setErrorMessage }) {
  const [sessionDates, setSessionDates] = useState([])

  useEffect(() => {
    moment.locale([txt('global.locale')])
  }, [])

  useEffect(() => {
    async function getSessionDates() {
      const sessionDatesTasks = availableDates.map(async (availableDate) => {
        const startDate = moment(availableDate.date).valueOf()
        const endDate = moment(availableDate.date).add(1, 'days').valueOf()
        const dates = await getAvailableSessions(startDate, endDate)
        return { ...availableDate, dates }
      })

      const sessionDates = await Promise.all(sessionDatesTasks)
      setSessionDates(sessionDates)
    }

    getSessionDates()
  }, [availableDates])

  return (
    <View style={styles.container}>
      <View>
        <CustomText font="bold" style={styles.titleBold} accessibilityRole="header" aria-level={2}>
          {txt('videoChat.availableSessions')}
        </CustomText>
        <CustomText style={[styles.text, sessionDates.length === 0 && styles.noSession]}>
          {sessionDates.length === 0 ? txt('videoChat.noAvailableSessions') : txt('videoChat.availableSessionInfo')}
        </CustomText>
      </View>

      {sessionDates.map((item, index) => (
        <DropDownContainer key={index} item={item} handleRefresh={handleRefresh} setErrorMessage={setErrorMessage} />
      ))}
    </View>
  )
}

function DropDownContainer({ item, handleRefresh, setErrorMessage }) {
  const [collapsed, setCollapsed] = useState(true)

  async function handleBookSession(sessionId) {
    const { bookingErrors } = txt('videoChat')

    const ERR_ENUMS = {
      LIMIT_REACHED: bookingErrors.limitReached,
      SESSION_NOT_FOUND: bookingErrors.SessionNotFound,
      SESSION_ALREADY_BOOKED: bookingErrors.alreadyBooked,
      OWN_BOOKING: bookingErrors.ownBooking,
      DOUBLE_BOOKING: bookingErrors.doubleBook,
      DATE_EXPIRED: bookingErrors.dateExpired,
      TIMEFRAME_OCCUPIED: bookingErrors.timeframeOccupied,
      VOLUNTEER_IS_ALREADY_IN_TIMEFRAME: bookingErrors.volunteerInTimeframe,
    }

    const { err_code } = await bookSession(sessionId)

    if (err_code !== undefined) {
      const errorMessage = ERR_ENUMS[err_code] ?? bookingErrors.unknown
      return setErrorMessage(errorMessage)
    }

    await handleRefresh()
  }

  if (collapsed) {
    return (
      <CustomPressable
        onPress={() => setCollapsed(false)}
        activeOpacity={0.8}
        style={styles.dropdownContainer}
        accessibilityRole="button"
        accessibilityLabel={txt('videoChat.availableSessionsLabel') + item.title}
      >
        <View style={styles.flexRow}>
          <CustomText>{moment(item.date).format('dddd[,] DD[.] MMMM YYYY')}</CustomText>

          <NormalIcon
            name={IconNames.arrowDown}
            color={Colors.inactive}
            style={styles.icon}
            size={Fontsizes.l}
            stroke={IconNames.stroke.solid}
          />
        </View>
      </CustomPressable>
    )
  }

  return (
    <View style={styles.dropdownContainer}>
      <CustomPressable
        onPress={() => setCollapsed(true)}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={
          txt('videoChat.availableSessionsHideLabel') + moment(item.date).format('dddd[,] DD[.] MMMM YYYY')
        }
      >
        <View style={styles.flexRow}>
          <CustomText>{moment(item.date).format('dddd[,] DD[.] MMMM YYYY')}</CustomText>

          <NormalIcon
            name={IconNames.arrowUp}
            color={Colors.inactive}
            style={styles.icon}
            size={Fontsizes.l}
            stroke={IconNames.stroke.solid}
          />
        </View>
      </CustomPressable>

      {item.dates.map((session, index) => {
        const style = { marginTop: 10 }

        if (index !== 0) {
          style.paddingTop = 10
          style.borderTopWidth = 1
          style.borderColor = Colors.shadow
        }

        const date = moment(session.start_date).format('dddd [d.] DD/MM [kl.] HH:mm')
        const dateString = date.charAt(0).toUpperCase() + date.slice(1)

        const duration = (new Date(session.end_date).getTime() - new Date(session.start_date).getTime()) / 1000 / 60
        const durationString = `${txt('videoChat.duration')}: ${duration} min.`

        return (
          <View key={session.id} style={[styles.flexRow, style]}>
            <View style={styles.flex2}>
              <CustomText font="small">{dateString}</CustomText>

              <CustomText font="small">{durationString}</CustomText>

              <CustomText font="small">
                {txt('videoChat.volunteer')}: {session.username}
              </CustomText>
            </View>

            <View style={styles.buttonContainer}>
              <RoundSquareBtn title={txt('videoChat.book')} onPress={() => handleBookSession(session.id)} />
            </View>
          </View>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  buttonContainer: {
    flex: 1,
    justifyContent: 'center',
  },

  container: {
    backgroundColor: Colors.background,
    margin: 10,
  },

  dropdownContainer: {
    backgroundColor: Colors.white,
    ...CornerStyles.borderRadiusS,
    marginTop: 10,
    padding: 10,
    ...ShadowStyles.shadowInputs,
  },

  flex2: {
    flex: 2,
  },

  flexRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  icon: {
    alignSelf: 'center',
    marginLeft: 'auto',
  },

  text: {
    color: Colors.inactive,
    marginTop: 10,
    textAlign: 'center',
  },

  noSession: {
    marginBottom: 60,
  },

  titleBold: {
    textAlign: 'center',
  },
})
